/**
 * Coalition and opposition, answer first.
 *
 * The balance of forces and the ranked "talk to first" list sit at the top,
 * because they are what an analyst has to walk into a meeting with. The graph
 * underneath is the evidence for them: who is tied to whom, which blocs exist,
 * and which ties cross between the camps.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Blocks,
  Compass,
  Network as NetworkIcon,
  Route,
  ServerCrash,
  Split,
  Tag,
  Users,
} from 'lucide-react';
import {
  Button,
  Card,
  Dialog,
  EmptyState,
  Pill,
  SectionHeader,
  SegmentedControl,
  Skeleton,
} from '../../../components/ui';
import { useMediaQuery, useReducedMotion } from '../../intelligence/network/ui';
import type { IssueView } from '../../../types/issues';
import { MapCanvas, MapCanvasHandle } from './MapCanvas';
import { NodePanel } from './NodePanel';
import { ForcesBar, IndexTrack, StanceGlyph, StanceLadder, StanceLegend } from './PositionScale';
import { MapGraphNode, PRIORITY_HINT, PRIORITY_LABEL, blocLabel, subjectKey } from './mapModel';
import { describeConcentration, describeIndex, formatIndex, labelOf } from './position';
import { useIssueMap } from './useIssueMap';

type Lens = 'blocs' | 'network';
type Mode = 'map' | 'list';

const PRIORITY_TONE: Record<string, 'amber' | 'neutral' | 'red' | 'green' | 'outline'> = {
  swing: 'amber',
  bridge: 'neutral',
  blocker: 'red',
  amplifier: 'green',
  unknown: 'outline',
};

export const IssueMapPanel: React.FC<{ issue: IssueView }> = ({ issue }) => {
  const { map, model, stances, loading, error, unavailable, reload } = useIssueMap(issue.id);
  const canvas = useRef<MapCanvasHandle>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [hoverId, setHoverId] = useState<string | null>(null);
  const [lens, setLens] = useState<Lens>('blocs');
  const [showLabels, setShowLabels] = useState(true);
  const phone = useMediaQuery('(max-width: 767px)');
  const reducedMotion = useReducedMotion();
  const [mode, setMode] = useState<Mode>('map');

  // A graph on a phone is a poor instrument: lead with the ranked list there
  useEffect(() => {
    setMode(phone ? 'list' : 'map');
  }, [phone]);

  const selected = selectedId ? model.byId.get(selectedId) || null : null;
  const balance = map?.balance || issue.balance;

  const select = useCallback(
    (id: string | null, fly = false) => {
      setSelectedId(id);
      if (id && fly) canvas.current?.flyTo(id, 2);
    },
    [],
  );

  const blocOf = useCallback(
    (node: MapGraphNode | null) => (node && node.bloc !== null ? model.blocs.find((b) => b.id === node.bloc) || null : null),
    [model],
  );

  /** Everyone grouped the way the map draws them, for the list affordance */
  const grouped = useMemo(() => {
    const groups = model.blocs
      .map((bloc) => ({
        key: `bloc-${bloc.id}`,
        label: blocLabel(bloc),
        meanPosition: bloc.meanPosition,
        members: bloc.memberIds.map((id) => model.byId.get(id)!).filter(Boolean),
      }))
      .filter((g) => g.members.length > 0);
    const placed = new Set(groups.flatMap((g) => g.members.map((m) => m.id)));
    const rest = model.nodes.filter((n) => !placed.has(n.id));
    if (rest.length) groups.push({ key: 'unaligned', label: 'Unaligned', meanPosition: 0, members: rest });
    groups.forEach((g) => g.members.sort((a, b) => b.influence - a.influence));
    return groups.sort((a, b) => b.members.length - a.members.length);
  }, [model]);

  if (loading) {
    return (
      <div className="space-y-4">
        <Card padded>
          <Skeleton className="h-4 w-40" />
          <Skeleton className="mt-3 h-2.5 w-full" />
          <Skeleton className="mt-4 h-3 w-64" />
        </Card>
        <Card padded>
          <Skeleton className="h-[320px] w-full" />
        </Card>
      </div>
    );
  }

  if (unavailable || (error && !map)) {
    return (
      <Card>
        <EmptyState
          icon={ServerCrash}
          title={error || 'The coalition map is unavailable'}
          action={
            <Button size="sm" variant="secondary" onClick={reload}>
              Try again
            </Button>
          }
        />
      </Card>
    );
  }

  if (model.nodes.length === 0) {
    return (
      <Card>
        <EmptyState icon={Users} title="No positions recorded yet — the map needs stances" />
      </Card>
    );
  }

  const priorities = (map?.priorities || []).slice(0, 6);
  const generated = map?.generatedAt ? new Date(map.generatedAt) : null;

  const nodePanel = selected && (
    <NodePanel
      node={selected}
      stance={stances.get(selected.id) || null}
      bloc={blocOf(selected)}
      neighbours={model.neighbours.get(selected.id) || []}
      onSelect={(id) => select(id, true)}
      onClose={() => select(null)}
    />
  );

  return (
    <div className="space-y-4">
      {/* ---------- the answer ---------- */}
      <Card padded>
        <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2">
          <div>
            <p className="text-[12px] text-neutral-500">Balance of forces</p>
            <p className="mt-1 flex items-baseline gap-2">
              <span className="text-[26px] leading-none tracking-[-0.02em] tabular-nums text-ink">{formatIndex(balance.index)}</span>
              <span className="text-[13px] text-neutral-500">{describeIndex(balance.index)}</span>
            </p>
          </div>
          <p className="max-w-[300px] text-right text-[11.5px] leading-relaxed text-neutral-400">
            Influence-weighted, not a headcount.
            {generated && ` Read at ${generated.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}.`}
          </p>
        </div>

        <IndexTrack index={balance.index} className="mt-3" />
        <ForcesBar balance={balance} className="mt-3" />

        <div className="mt-3 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-neutral-200/70 pt-3">
          <StanceLegend
            counts={{
              2: balance.counts?.champion,
              1: balance.counts?.supportive,
              0: balance.counts?.neutral,
              [-1]: balance.counts?.opposed,
              [-2]: balance.counts?.stronglyOpposed,
              unknown: model.counts.unknown,
            }}
          />
          <p className="text-[11.5px] text-neutral-400">{describeConcentration(balance.concentration)}</p>
        </div>
      </Card>

      {/* ---------- who to talk to first ---------- */}
      <Card divided>
        <div className="px-4 pb-1 pt-3.5">
          <SectionHeader
            title="Talk to first"
            aside={
              <span className="hidden sm:inline">
                {priorities.length ? 'Ranked by what their movement is worth' : ''}
              </span>
            }
            className="mb-0"
          />
        </div>
        {priorities.length === 0 ? (
          <EmptyState icon={Compass} title="No one stands out yet — record a few more positions" />
        ) : (
          <ul>
            {priorities.map((priority, index) => {
              const id = subjectKey(priority.subject);
              const node = model.byId.get(id);
              return (
                <li key={`${id}-${index}`}>
                  <button
                    type="button"
                    onClick={() => {
                      select(id, true);
                      if (phone) setMode('list');
                    }}
                    onMouseEnter={() => setHoverId(node ? id : null)}
                    onMouseLeave={() => setHoverId(null)}
                    disabled={!node}
                    className="flex w-full items-start gap-3 px-4 py-2.5 text-left transition-colors hover:bg-neutral-50 disabled:cursor-default disabled:hover:bg-transparent"
                  >
                    <span className="mt-0.5 w-4 flex-shrink-0 text-[12px] tabular-nums text-neutral-400">{index + 1}</span>
                    <StanceGlyph position={node ? node.node.position : null} className="mt-0.5" />
                    <span className="min-w-0 flex-1">
                      <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                        <span className="truncate text-[13px] text-ink">{priority.subject.label}</span>
                        <Pill tone={PRIORITY_TONE[priority.kind] || 'neutral'} title={PRIORITY_HINT[priority.kind]}>
                          {PRIORITY_LABEL[priority.kind] || priority.kind}
                        </Pill>
                      </span>
                      <span className="mt-0.5 block text-[12px] leading-relaxed text-neutral-500">{priority.reason}</span>
                    </span>
                    {node && <StanceLadder position={node.node.position} className="mt-1 hidden sm:inline-flex" />}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {/* ---------- the evidence ---------- */}
      <Card className="overflow-visible">
        <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-3">
          <SectionHeader
            title="The network"
            aside={
              <span className="hidden sm:inline">
                {model.nodes.length} actors · {model.crossings.length}{' '}
                {model.crossings.length === 1 ? 'tie' : 'ties'} across camps
              </span>
            }
            className="mb-0"
          />
          <div className="flex flex-wrap items-center gap-2">
            {phone && (
              <SegmentedControl
                label="View"
                value={mode}
                onChange={(next) => setMode(next as Mode)}
                items={[
                  { value: 'list', label: 'List' },
                  { value: 'map', label: 'Graph' },
                ]}
              />
            )}
            {mode === 'map' && (
              <>
                <SegmentedControl
                  label="Layout"
                  value={lens}
                  onChange={(next) => setLens(next as Lens)}
                  items={[
                    { value: 'blocs', label: 'Blocs', title: 'Group the network into the blocs found in the relationships' },
                    { value: 'network', label: 'Free', title: 'Let the whole network settle on its own' },
                  ]}
                />
                <button
                  type="button"
                  onClick={() => setShowLabels((v) => !v)}
                  aria-pressed={showLabels}
                  className={`inline-flex h-8 items-center gap-1.5 rounded-full px-3 text-[12px] transition-colors ${
                    showLabels ? 'bg-neutral-100 text-ink' : 'text-neutral-500 hover:bg-neutral-50'
                  }`}
                >
                  <Tag className="h-3.5 w-3.5" strokeWidth={1.75} />
                  Names
                </button>
              </>
            )}
          </div>
        </div>

        {mode === 'map' ? (
          <>
          <div className="relative h-[clamp(340px,60vh,560px)] w-full border-t border-neutral-200/70">
            <MapCanvas
              ref={canvas}
              model={model}
              lens={lens}
              selectedId={selectedId}
              hoverId={hoverId}
              showLabels={showLabels}
              reducedMotion={reducedMotion}
              onSelect={(id) => select(id)}
            />
            {!phone && selected && (
              <div className="absolute bottom-3 right-3 top-3 z-20 flex w-[300px] animate-fade-up flex-col overflow-hidden rounded-2xl bg-white shadow-[0_10px_40px_-16px_rgba(0,0,0,0.3)] ring-1 ring-black/[0.06] motion-reduce:animate-none">
                {nodePanel}
              </div>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 border-t border-neutral-200/70 px-4 py-2.5">
            <StanceLegend />
            <span className="flex items-center gap-1.5 text-[11.5px] text-neutral-500">
              <span className="h-[2px] w-4 rounded-full bg-amber-500" />
              Tie across camps
            </span>
            <span className="flex items-center gap-1.5 text-[11.5px] text-neutral-500" title="Bridges stand in both camps; swing actors are undecided but heavy">
              <Route className="h-3.5 w-3.5 text-neutral-400" strokeWidth={1.75} />
              Ring = bridge · dashed = swing
            </span>
          </div>
          </>
        ) : (
          <div className="border-t border-neutral-200/70">
            {grouped.map((group) => (
              <section key={group.key} className="border-b border-neutral-200/70 last:border-b-0">
                <div className="flex items-center justify-between gap-2 bg-neutral-50/60 px-4 py-2">
                  <p className="flex min-w-0 items-center gap-1.5 text-[12px] text-ink">
                    <Blocks className="h-3.5 w-3.5 flex-shrink-0 text-neutral-400" strokeWidth={1.75} />
                    <span className="truncate">{group.label}</span>
                  </p>
                  <p className="flex-shrink-0 text-[11.5px] text-neutral-400">
                    {group.members.length} · {labelOf(group.meanPosition)}
                  </p>
                </div>
                <ul className="divide-y divide-neutral-200/70">
                  {group.members.map((member) => (
                    <li key={member.id}>
                      <button
                        type="button"
                        onClick={() => select(member.id)}
                        className="flex w-full items-center gap-2.5 px-4 py-2.5 text-left transition-colors hover:bg-neutral-50"
                      >
                        <StanceGlyph position={member.node.position} />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[13px] leading-tight text-ink">{member.label}</span>
                          <span className="block truncate text-[11.5px] text-neutral-500">
                            {labelOf(member.node.position)}
                            {member.influence > 0 && ` · influence ${Math.round(member.influence * 100)}`}
                          </span>
                        </span>
                        <span className="flex flex-shrink-0 items-center gap-1">
                          {member.node.isBridge && <Route className="h-3.5 w-3.5 text-neutral-400" strokeWidth={1.75} />}
                          {member.node.isSwing && <Split className="h-3.5 w-3.5 text-amber-500" strokeWidth={1.75} />}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
      </Card>

      {/* ---------- blocs ---------- */}
      {model.blocs.length > 0 && mode === 'map' && (
        <Card divided>
          <div className="px-4 pb-1 pt-3.5">
            <SectionHeader
              title="Blocs"
              aside={<span className="hidden sm:inline">Communities in the relationship network</span>}
              className="mb-0"
            />
          </div>
          <ul>
            {model.blocs.map((bloc) => (
              <li key={bloc.id}>
                <button
                  type="button"
                  onClick={() => canvas.current?.fit(bloc.memberIds)}
                  className="flex w-full items-center gap-3 px-4 py-2.5 text-left transition-colors hover:bg-neutral-50"
                >
                  <StanceGlyph position={bloc.meanPosition} />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] text-ink">{blocLabel(bloc)}</span>
                    <span className="block truncate text-[11.5px] text-neutral-500">
                      {bloc.size} {bloc.size === 1 ? 'actor' : 'actors'} · {labelOf(bloc.meanPosition)} on average
                    </span>
                  </span>
                  <span className="flex-shrink-0 text-[11.5px] tabular-nums text-neutral-400">
                    {Math.round(bloc.influence > 1 ? bloc.influence : bloc.influence * 100)} weight
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}

      {/* ---------- phones: the panel is a sheet ---------- */}
      <Dialog open={!!(phone && selected)} onClose={() => select(null)} handle label="Actor" className="sm:max-w-md">
        {/* a flex column, so the panel's own list scrolls instead of being cut off at the cap */}
        <div className="flex max-h-[78vh] min-h-0 flex-col overflow-hidden">{nodePanel}</div>
      </Dialog>

      {error && map && <p className="rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">{error}</p>}

      {model.nodes.length > 0 && model.links.length === 0 && (
        <p className="flex items-center gap-1.5 text-[11.5px] text-neutral-400">
          <NetworkIcon className="h-3.5 w-3.5" strokeWidth={1.75} />
          No relationships recorded between these actors — the map shows stances only.
        </p>
      )}
    </div>
  );
};

export default IssueMapPanel;
